import React, { useRef, useState, useEffect, useCallback } from 'react';
import { styles } from '../styles';
import { Box, Skeleton, Stack, IconButton, Tabs, Tab } from '@mui/material';
import { ArrowCircleLeft as ArrowCircleLeftIcon } from '@mui/icons-material';
import Questions from '../Questions/Questions';
import { IApprovalTemplate } from '../types';
import StyledSnackbar from '../../Components/CustomUIElements/StyledSnackbar';
import Api from '../API';
import { formatUserPermissions } from '../../Components/Utilities/formatUserPermissions';
const BASE_URL = window.location.origin;

export default function ApprovalTemplate({
  approvalTemplateId,
  entityId,
  enableEmailTemplateFields
}: {
  approvalTemplateId: number;
  entityId: number;
  enableEmailTemplateFields?: boolean;
}) {
  const [indicatorWidth, setIndicatorWidth] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [approvalTemplateData, setApprovalTemplateData] = useState<IApprovalTemplate>({});
  const [snackbar, setSnackbar] = useState<{
    message: string;
    state: 'success' | 'warning' | 'error';
  }>({
    message: '',
    state: 'success'
  });

  const headerRefs = useRef<HTMLDivElement[]>([]);

  const userPermissions = formatUserPermissions();

  const getApprovalTemplate = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await Api.getApprovalTemplate(approvalTemplateId);
      setApprovalTemplateData(data.res);
    } catch (err) {
      setSnackbar({
        message: `Failed to fetch approval template data, ${err}`,
        state: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, [approvalTemplateId]);

  useEffect(() => {
    setIndicatorWidth(headerRefs.current[0].clientWidth - 32);
  }, []);

  useEffect(() => {
    getApprovalTemplate();
  }, [getApprovalTemplate]);

  return (
    <Box sx={styles.jobPageContainer}>
      {isLoading ? (
        <Skeleton animation="wave" sx={{ marginBottom: '20px' }} width={200} height={56} />
      ) : (
        <Box sx={styles.jobPageHeader}>
          <h1>{approvalTemplateData.name}</h1>
        </Box>
      )}
      <Box sx={styles.tabsContainer}>
        <Tabs
          value={0}
          aria-label="Edit Approval Template Fields"
          sx={{
            ...styles.tabs,
            '& .MuiTabs-indicator': { ...styles.tabIndicator, maxWidth: indicatorWidth + 'px' }
          }}
        >
          <Tab ref={(el) => (headerRefs.current[0] = el)} label="Add/Edit fields" />
        </Tabs>
      </Box>
      {isLoading ? (
        <Skeleton animation="wave" sx={{ marginBottom: '20px' }} width={200} height={56} />
      ) : (
        <Box sx={styles.backButton}>
          <Stack
            flexDirection="row"
            alignItems="center"
            onClick={() => {
              window.location.href = `${BASE_URL}/admin/requisitions?templates`;
            }}
          >
            <IconButton>
              <ArrowCircleLeftIcon />
            </IconButton>
            <Box>Back to approval templates</Box>
          </Stack>
        </Box>
      )}
      <Box>
        {isLoading ? (
          <Skeleton animation="wave" sx={{ marginBottom: '20px' }} width={200} height={56} />
        ) : (
          <Questions
            entityId={entityId}
            approvalTemplateId={approvalTemplateId}
            approvalTemplateData={approvalTemplateData}
            setApprovalTemplateData={setApprovalTemplateData}
            getApprovalTemplate={getApprovalTemplate}
            enableEmailTemplateFields={enableEmailTemplateFields}
          />
        )}
      </Box>
      <StyledSnackbar
        message={snackbar.message}
        state={snackbar.state}
        setSnackbarState={setSnackbar}
      />
    </Box>
  );
}
